/**
 * home-plugin — the unified `/home` overview surface as a studio-sibling
 * `ComposableGenomePlugin` (#555).
 *
 * Mirrors `devicePlugin`: one `component` editor (`home`) routed at `/home`,
 * lowering onto `component`/`route` only (NO new `EntityKind` — the Home surface
 * is an AGGREGATION of existing surfaces, not an engine primitive; gate 4 stays
 * green, the route count derives +1). The editor is `React.lazy` so the whole
 * home aggregate (the cards, the per-card telemetry hooks, the hb simulate core)
 * stays in a split chunk OUT of the app entry (gate 8 — the eager module carries
 * only the route + the `React.lazy` boundary).
 *
 * The RIGHT-region IDE dock panel is registered SEPARATELY by `home-dock.tsx`,
 * side-effect-imported from the off-entry boot lane (`dev-harness-boot`) — so its
 * Suspense-wrapper + registration weight rides the boot chunk, NOT the eager app
 * entry (the #515 device-dock reclaim). The `/home` route works regardless.
 *
 * @module @ubiclouds/v5-studio/home/home-plugin
 */

import { lazy } from 'react';
import { definePlugin } from '@ubiclouds/v5-sdk/plugins';
import type { ComposableGenomePlugin } from '@ubiclouds/v5-sdk/plugins';

/** The Home-surface plugin id. */
export const HOME_PLUGIN_ID = 'v5-studio-home';

/** The split-chunk Home surface (the `/home` route editor + the dock panel body). */
export const LazyHomeSurface = lazy(() => import('./HomeSurface.js'));

/**
 * The Home plugin — the `/home` route → the lazy `home` editor. Composed as a
 * SIBLING in `studioPlugins`; it adds no new `EntityKind` (the surface lowers onto
 * `component`/`route`), so gate 4 stays green.
 */
export const homePlugin: ComposableGenomePlugin = definePlugin(HOME_PLUGIN_ID)
  .name('Home')
  .trustTier('core')
  .editor('home', LazyHomeSurface)
  .route('/home', { routesTo: 'component/home', title: 'Home' })
  .build();
